import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'

import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { useCurrentApplication } from '../useCurrentApplication'

import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

type MessagesApplicationsPage = RestSchemas['Messages.Applications.Page']
type MessagesApplicationsApplicationPages = RestSchemas['Messages.Applications.ApplicationPages']

const formSchema = z.object({
  id: z
    .string({ required_error: 'Page ID is required' })
    .trim()
    .min(1, 'Page ID is required')
    .max(128, 'Page ID cannot exceed 128 characters'),
  description: z
    .string()
    .max(256, 'Description cannot exceed 256 characters')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  pageInfo: z
    .string()
    .max(2000, 'Page info cannot exceed 2000 characters')
    .optional()
    .or(z.literal('').transform(() => undefined)),
})

export type TPageForm = z.infer<typeof formSchema>

type PageFormProps = {
  onSubmit?: (page: TPageForm) => void
  sheetTrigger?: ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedPage?: MessagesApplicationsPage
  initialValues?: Partial<TPageForm>
}

export const PageForm = (props: PageFormProps) => {
  const { applicationId } = useCurrentApplication()
  const {
    selectedPage,
    sheetTrigger,
    openInSheet,
    sheetDescription,
    sheetTitle,
    sheetTriggerTooltip,
    onSubmit,
    initialValues,
  } = props

  const [sheetOpen, setSheetOpen] = useState(!!openInSheet)
  const edit = !!selectedPage

  const { getAppPagesApiGetApplicationPages, postAppPagesApiSaveApplicationPages } = useRestClient()

  const {
    data: pagesData,
    isPending: isPagesLoading,
    isError: isPagesError,
  } = getAppPagesApiGetApplicationPages.useQuery<MessagesApplicationsApplicationPages>({
    query: { applicationId },
  })

  const { mutateAsync: savePages, isPending: isSaving } = postAppPagesApiSaveApplicationPages.useMutation()

  const invalidatePages = useCallback(() => {
    getAppPagesApiGetApplicationPages.invalidateQueries({ parameters: { query: { applicationId } } })
  }, [applicationId, getAppPagesApiGetApplicationPages])

  const closeSheetIfNeeded = useCallback(() => {
    if (sheetTrigger || openInSheet) {
      setSheetOpen(false)
    }
  }, [openInSheet, sheetTrigger])

  const submitHandler = useCallback(
    async (formData: TPageForm) => {
      const trimmedId = formData.id.trim()
      const currentItems = Array.isArray(pagesData?.items)
        ? (pagesData.items?.filter((item): item is MessagesApplicationsPage => !!item?.id) ?? [])
        : []

      if (!edit && currentItems.some((item) => item.id?.trim() === trimmedId)) {
        toast.error('A page with this ID already exists')
        return false
      }

      const sanitizedPage: MessagesApplicationsPage = {
        id: trimmedId,
        description: formData.description?.trim() || undefined,
        pageInfo: formData.pageInfo?.trim() || undefined,
      }

      const updatedItems =
        edit && selectedPage
          ? currentItems.map((item) => (item.id === selectedPage.id ? { ...item, ...sanitizedPage } : item))
          : [...currentItems, sanitizedPage]

      const payload: MessagesApplicationsApplicationPages = { items: updatedItems }

      try {
        await savePages({ query: { applicationId }, body: payload })
        invalidatePages()
        closeSheetIfNeeded()
        toast.success(edit ? 'Page updated successfully' : 'Page created successfully')
        onSubmit?.(formData)
        return true
      } catch (error) {
        console.error('Failed to save page', error)
        toast.error(`Failed to ${edit ? 'update' : 'create'} page`)
        return false
      }
    },
    [applicationId, closeSheetIfNeeded, edit, invalidatePages, pagesData, onSubmit, savePages, selectedPage]
  )

  const isLoading = isPagesLoading || isSaving

  if (isLoading) return <FormComponentLoading />
  if (isPagesError || !pagesData) return <ErrorComponent />

  const hasSheet = sheetTrigger || openInSheet
  const resolvedSheetTitle = sheetTitle ?? (edit ? 'Edit Page' : 'Create Page')
  const resolvedSheetDescription =
    sheetDescription ?? (edit ? 'Update the page details for this application' : 'Add a new page to this application')

  return hasSheet ? (
    <SheetWithFormComponent
      title={resolvedSheetTitle}
      description={resolvedSheetDescription}
      trigger={sheetTrigger}
      triggerTooltip={sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent
        selectedPage={selectedPage}
        initialValues={initialValues}
        submitHandler={submitHandler}
        isSubmitting={isSaving}
      />
    </SheetWithFormComponent>
  ) : (
    <FormComponent
      selectedPage={selectedPage}
      initialValues={initialValues}
      submitHandler={submitHandler}
      isSubmitting={isSaving}
    />
  )
}

const FormComponent = ({
  selectedPage,
  initialValues,
  submitHandler,
  isSubmitting,
}: {
  selectedPage?: MessagesApplicationsPage
  initialValues?: Partial<TPageForm>
  submitHandler: (data: TPageForm) => Promise<boolean>
  isSubmitting: boolean
}) => {
  const edit = !!selectedPage

  const defaultValues = useMemo(
    () => ({
      id: selectedPage?.id ?? initialValues?.id ?? '',
      description: selectedPage?.description ?? initialValues?.description ?? '',
      pageInfo: selectedPage?.pageInfo ?? initialValues?.pageInfo ?? '',
    }),
    [
      initialValues?.description,
      initialValues?.id,
      initialValues?.pageInfo,
      selectedPage?.description,
      selectedPage?.id,
      selectedPage?.pageInfo,
    ]
  )

  const form = useForm<TPageForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const handleSubmit = useCallback(
    async (values: TPageForm) => {
      const success = await submitHandler(values)
      if (success && !edit) {
        form.reset({ id: '', description: '', pageInfo: '' })
      }
    },
    [edit, form, submitHandler]
  )

  const submitLabel = isSubmitting ? 'Saving...' : edit ? 'Update Page' : 'Create Page'

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Page ID *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter page ID" disabled={edit} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter page description" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pageInfo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Page Info</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={4} placeholder="Enter additional page information" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="form-action-container">
            <Button type="submit" disabled={isSubmitting}>
              {submitLabel}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  )
}

export default PageForm
